import React, { useCallback, useEffect, useState } from 'react';
import {
  Button as MuiButton,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
} from '@mui/material';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { PortalConfigSelector } from '../../../store/config/selectors';
import {
  ThunkRequestTechnicianVerification,
  ThunkRequestTechnicianVerificationOnPortalSide,
  ThunkCheckCodeVerifyTechnician,
} from 'store/verificationRequest/thunks';
import { ReactComponent as ArrowIcon } from 'assets/arrowForward.icon.svg';

import {
  TechnicianConfirmationSelector,
  VerificationModalOpenSelector,
} from 'store/verificationRequest/selectors';
import {
  showTechnicianConfirmationModalHandler,
  verificationModalHandler,
  removeTechnicianConfirmationRequest,
} from 'store/verificationRequest';

import { ReactComponent as PersonIcon } from 'assets/verify.icon.svg';
import { ReactComponent as SearchIcon } from 'assets/search.svg';

import Button from 'components/Button/Button';

import Input from '../../../components/Input/Input';

import { Formik } from 'formik';
import { enqueueSnackbar } from 'notistack';

import useSharedStyles from '../../../sharedStyles';

import useStyles from '../styles';
import { tt } from '@i18n/tt';

interface IProps {
  showAsButton?: boolean;
}

const TechnicianVerification: React.FC<IProps> = ({ showAsButton }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [openEnterCodeDialog, setOpenEnterCodeDialog] = useState<boolean>(false);
  const [isLocalVerificationModalOpen, setVerificationModalIsLocalOpen] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');
  const [requestId, setRequestId] = useState<number>(0);

  const config = useAppSelector(PortalConfigSelector);
  const openDialog = useAppSelector(VerificationModalOpenSelector);
  const verificationRequest = useAppSelector(TechnicianConfirmationSelector);

  const dispatch = useAppDispatch();
  const classes = useStyles();
  const sharedStyles = useSharedStyles();

  useEffect(() => {
    if (verificationRequest && !openDialog) {
      dispatch(showTechnicianConfirmationModalHandler(true));
    }
  }, [verificationRequest]);

  const sendVerificationRequest = useCallback(() => {
    setLoading(true);

    if (config.verificationTechType === 'ClientPortalToEnterCode') {
      dispatch(ThunkRequestTechnicianVerificationOnPortalSide())
        .unwrap()
        .then((res: any) => {
          setRequestId(res.data);
          setOpenEnterCodeDialog(true);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      dispatch(ThunkRequestTechnicianVerification())
        .unwrap()
        .then(({ code: verificationCode }) => {
          setCode(verificationCode);
          dispatch(verificationModalHandler(true));
          setVerificationModalIsLocalOpen(true)
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, []);

  const handleSubmit = useCallback(
    (values: any) => {
      setLoading(true);
      dispatch(ThunkCheckCodeVerifyTechnician({ code: values.code, requestId }))
        .unwrap()
        .then(() => {
          enqueueSnackbar('Technician successfully verified', { variant: 'success' });
          setOpenEnterCodeDialog(false);
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    },
    [requestId],
  );

  return (
    <>
      {loading && !showAsButton && <CircularProgress className={classes.loader} />}

      {showAsButton ? (
        <MuiButton
          type="button"
          onClick={() => {
            sendVerificationRequest();
          }}
          variant="text"
          fullWidth
          className={classes.verifyButton}
          startIcon={<SearchIcon/>}
          endIcon={
            loading ? (
              <CircularProgress className={classes.processingCircle} />
            ) : (
              <ArrowIcon />
            )
          }
        >
          {tt('pages.chats.verifyButton')}
        </MuiButton>
      ) : (
        <Tooltip title={tt('common.verifyTechnician')}>
          <PersonIcon
            onClick={() => {
              sendVerificationRequest();
            }}
          />
        </Tooltip>
      )}

      {openDialog && isLocalVerificationModalOpen && (
        <Dialog
          open
          onClose={() => {
            dispatch(verificationModalHandler(false));
            setVerificationModalIsLocalOpen(false)
            dispatch(removeTechnicianConfirmationRequest());
          }}
        >
          <DialogTitle>{tt('components.verifyTech.title')}</DialogTitle>
          <DialogContent>
            <p>{tt('components.verifyTech.saveHint')}</p>
            <div className={classes.code}>{code}</div>
            <p>{tt('components.verifyTech.mustSay')}</p>
            {verificationRequest && (
              <div className={classes.techName}>
                <b>
                  {verificationRequest?.techName}{' '}
                  {tt('components.verifyTech.verifiedTheirIdentity')}
                </b>
              </div>
            )}
            <div className={classes.dialogButton}>
              <Button
                text={tt('buttons.close')}
                onClick={() => {
                  dispatch(verificationModalHandler(false));
                  setVerificationModalIsLocalOpen(false)
                  dispatch(removeTechnicianConfirmationRequest());
                  setCode('');
                }}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {openEnterCodeDialog && (
        <Dialog
          open
          onClose={() => {
            setOpenEnterCodeDialog(false);
          }}
        >
          <DialogTitle>{tt('components.verifyTech.title')}</DialogTitle>
          <DialogContent>
            {tt('components.verifyTech.saveHint')}
            <Formik initialValues={{ code: '' }} onSubmit={handleSubmit}>
              {formik => (
                <form onSubmit={formik.handleSubmit}>
                  <Input label="Code" name="code" formik={formik} />
                  <div className={sharedStyles.dialogActions}>
                    <Button
                      type="submit"
                      text={tt('buttons.confirm')}
                      disabled={loading}
                    />
                  </div>
                </form>
              )}
            </Formik>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default TechnicianVerification;
